import {
  DopamineProgressItem,
  DopamineRunEvent,
  DopamineRunProgress,
} from './DopamineAssistant.types'

export const createDopamineRunProgress = (): DopamineRunProgress => ({
  endedAt: null,
  failureMessage: null,
  items: [],
  runId: null,
  startedAt: null,
  status: 'idle',
})

const startItem = (
  state: DopamineRunProgress,
  event: Extract<DopamineRunEvent, {type: 'STEP_STARTED' | 'TOOL_CALL_START'}>,
): DopamineRunProgress => {
  const isToolWrappedByVisibleStep =
    event.type === 'TOOL_CALL_START' &&
    state.items.some(
      item =>
        item.kind === 'step' &&
        item.status === 'active' &&
        item.activeLabel === event.activeLabel,
    )

  if (isToolWrappedByVisibleStep) return state

  const item: DopamineProgressItem = {
    activeLabel: event.activeLabel,
    completedLabel: event.completedLabel,
    id: event.itemId,
    kind: event.type === 'STEP_STARTED' ? 'step' : 'tool',
    status: 'active',
  }
  const completedItems: DopamineProgressItem[] = state.items.map(currentItem =>
    currentItem.status === 'active'
      ? {...currentItem, status: 'completed'}
      : currentItem,
  )
  const existingIndex = state.items.findIndex(({id}) => id === event.itemId)

  if (existingIndex === -1) {
    return {...state, items: [...completedItems, item]}
  }

  return {
    ...state,
    items: completedItems.map((currentItem, index) =>
      index === existingIndex ? item : currentItem,
    ),
  }
}

const completeItem = (
  state: DopamineRunProgress,
  event: Extract<
    DopamineRunEvent,
    {type: 'STEP_FINISHED' | 'TOOL_CALL_RESULT'}
  >,
): DopamineRunProgress => ({
  ...state,
  items: state.items.map(item =>
    item.id === event.itemId
      ? {
          ...item,
          completedLabel: event.completedLabel ?? item.completedLabel,
          status: 'completed',
        }
      : item,
  ),
})

export const updateDopamineRunProgress = (
  state: DopamineRunProgress,
  event: DopamineRunEvent,
): DopamineRunProgress => {
  if (event.type === 'RUN_STARTED') {
    return {
      endedAt: null,
      failureMessage: null,
      items: [],
      runId: event.runId,
      startedAt: event.occurredAt,
      status: 'active',
    }
  }

  if (event.type === 'RUN_ERROR' && state.status === 'idle') {
    return {
      ...state,
      endedAt: event.occurredAt,
      failureMessage: event.displayMessage,
      runId: event.runId,
      status: 'failed',
    }
  }

  if (event.runId !== state.runId || state.status !== 'active') return state

  switch (event.type) {
    case 'STEP_STARTED':
    case 'TOOL_CALL_START':
      return startItem(state, event)
    case 'STEP_FINISHED':
    case 'TOOL_CALL_RESULT':
      return completeItem(state, event)
    case 'TOOL_CALL_END':
      // END closes the streamed tool arguments. RESULT marks the tool work done.
      return state
    case 'RUN_FINISHED':
      return {
        ...state,
        endedAt: event.occurredAt,
        items: state.items.map(item => ({...item, status: 'completed'})),
        status: 'completed',
      }
    case 'RUN_ERROR':
      return {
        ...state,
        endedAt: event.occurredAt,
        failureMessage: event.displayMessage,
        items: state.items.map(item =>
          item.status === 'active' ? {...item, status: 'failed'} : item,
        ),
        status: 'failed',
      }
    default:
      return state
  }
}
